import { Campaign, Language, UserDto, UserPreferenceDto } from '@agroone/entities'
import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { map, Observable } from 'rxjs'
import { API_URL } from '../../models/environment.token'
import { requireDefined } from '../../utils/require-defined.util'

/**
 * Shared HTTP access to the user-preferences endpoints.
 *
 * The API is common to every front app, so the service lives in the lib and
 * only depends on the {@link API_URL} token. It can be injected directly by any
 * app (agroone, agropilot, masterdata) without extending a base service.
 */
@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService {
  private readonly http: HttpClient = inject(HttpClient)
  private readonly apiUrl: string = inject(API_URL)
  private readonly usersUrl: string = `${this.apiUrl}/users`
  private readonly languagesUrl: string = `${this.apiUrl}/languages`
  private readonly campaignsUrl: string = `${this.apiUrl}/campaigns`

  public updateUserPreference(preferences: UserPreferenceDto): Observable<UserDto> {
    return this.http.put<UserDto>(`${this.usersUrl}/me`, preferences)
  }

  public getLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(this.languagesUrl)
  }

  public getCampaigns(): Observable<Campaign[]> {
    return this.http
      .get<{ data: Campaign[] }>(this.campaignsUrl)
      .pipe(
        map((res) =>
          (res.data ?? []).sort((a, b) => requireDefined(a.key, 'the campaign key').localeCompare(String(b.key))),
        ),
      )
  }
}
